import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  ArrowRight, Check, Sparkles, Clock, Calendar, Layers, Film, Image as ImageIcon,
  Layout, Mic, UserCircle2, MessageCircle, Send, Mail, Phone, Star, Zap,
  TrendingUp, Eye, Building2, Bot, Settings2, Megaphone, Flame, Lock, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import SocialNav from "@/components/social/SocialNav";
import SocialFooter from "@/components/social/SocialFooter";
import useSEO from "@/hooks/useSEO";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP =
  "https://wa.me/17865756919?text=Hi%21%20I%27d%20like%20to%20learn%20about%20the%20Social%20Media%20Care%20Package.";

/* ---------------- Reusable bits ---------------- */
const SectionHeader = ({ eyebrow, title, lede, dark = false, accent = "text-cyan-500", testid }) => (
  <div data-testid={testid} className="text-center max-w-3xl mx-auto fade-up">
    {eyebrow && (
      <div
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-black tracking-widest uppercase ${
          dark
            ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/20"
            : "bg-cyan-50 text-cyan-700 border border-cyan-100"
        }`}
      >
        <Sparkles size={12} className={accent} /> {eyebrow}
      </div>
    )}
    <h2 className={`font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight ${dark ? "text-white" : "text-slate-900"}`}>
      {title}
    </h2>
    {lede && <p className={`mt-5 text-base sm:text-lg ${dark ? "text-slate-300" : "text-slate-600"} leading-relaxed`}>{lede}</p>}
  </div>
);

const PriceCard = ({ name, price, suffix = "/month", bestFor, items, cta, note, badge, highlight = false, accent = "cyan", testid }) => {
  const accentMap = {
    cyan: { ring: "ring-cyan-400/40", glow: "0 30px 80px -20px rgba(34,211,238,0.45)", btn: "bg-cyan-400 hover:bg-cyan-300 text-slate-900", badge: "bg-cyan-400 text-slate-900" },
    fuchsia: { ring: "ring-fuchsia-400/40", glow: "0 30px 80px -20px rgba(217,70,239,0.45)", btn: "bg-fuchsia-500 hover:bg-fuchsia-400 text-white", badge: "bg-fuchsia-500 text-white" },
    amber: { ring: "ring-amber-400/40", glow: "0 30px 80px -20px rgba(251,191,36,0.45)", btn: "bg-amber-400 hover:bg-amber-300 text-slate-900", badge: "bg-amber-400 text-slate-900" },
  };
  const a = accentMap[accent];
  return (
    <div
      data-testid={testid}
      className={`relative h-full rounded-[2rem] p-7 sm:p-8 fade-up flex flex-col ${
        highlight
          ? `bg-gradient-to-br from-white to-cyan-50 border border-cyan-200 ring-1 ${a.ring} scale-[1.02]`
          : "bg-white border border-slate-100"
      } shadow-[var(--card-shadow)] hover:-translate-y-1 transition-transform`}
      style={{ "--card-shadow": highlight ? a.glow : "0 10px 30px -10px rgba(15,23,42,0.15)" }}
    >
      {badge && (
        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full ${a.badge} px-4 py-1 font-black text-[11px] tracking-widest uppercase shadow-lg`}>
          <Sparkles size={12} /> {badge}
        </div>
      )}
      <div className="text-xs uppercase tracking-widest font-black text-slate-500">{name}</div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="font-display text-5xl font-black text-slate-900 leading-none">{price}</span>
        <span className="text-slate-500 text-sm font-medium">{suffix}</span>
      </div>
      {bestFor && <p className="mt-3 text-sm text-slate-600 italic leading-relaxed">{bestFor}</p>}
      <ul className="mt-6 space-y-2.5 flex-1">
        {items.map((t, i) => (
          <li key={i} className="flex items-start gap-2.5 text-[15px] text-slate-700 leading-snug">
            <Check size={16} className={`mt-1 shrink-0 ${highlight ? "text-cyan-600" : "text-emerald-500"}`} />
            <span>{t}</span>
          </li>
        ))}
      </ul>
      <a
        href="#contact"
        data-testid={`${testid}-cta`}
        className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full font-bold px-6 py-3.5 transition hover:-translate-y-0.5 ${a.btn}`}
      >
        {cta} <ArrowRight size={16} />
      </a>
      {note && <p className="mt-4 text-xs text-slate-500 leading-relaxed">{note}</p>}
    </div>
  );
};

/* ===================== HERO ===================== */
const Hero = () => (
  <section id="home" data-testid="social-hero" className="relative pt-32 md:pt-40 pb-24 md:pb-32 bg-[#0B0F19] text-white overflow-hidden">
    <div className="absolute inset-0 hero-grid pointer-events-none" />
    <div className="absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-cyan-500/20 blur-[140px] pointer-events-none" />
    <div className="absolute -bottom-40 -right-40 h-[520px] w-[520px] rounded-full bg-fuchsia-600/20 blur-[140px] pointer-events-none" />

    <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7 fade-up">
        <div className="inline-flex items-center gap-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 px-4 py-1.5 text-xs sm:text-sm font-black tracking-widest uppercase text-cyan-300">
          <Sparkles size={14} /> The Social Media Care Package
        </div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mt-5 leading-[1.04]">
          Stop Stressing About What To Post.
          <span className="block mt-2 bg-gradient-to-r from-cyan-300 via-cyan-400 to-fuchsia-400 bg-clip-text text-transparent">
            We’ll Take Over Your Social Media For You.
          </span>
        </h1>
        <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl leading-relaxed">
          Instant Site Revival helps local businesses create consistent, professional, scroll-stopping
          content across Instagram, Facebook, TikTok, and YouTube Shorts — without you having to film,
          edit, write captions, or figure out what to post every day.
        </p>
        <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-2 max-w-2xl">
          {[
            "Done-for-you Reels, Stories, Posts, and Carousels",
            "Monthly content calendar planned for your business",
            "AI-assisted content creation to produce more, faster",
            "Optional AI avatar clone — show up online without filming daily",
            "Built for local businesses that want trust, visibility, and customers",
          ].map((b, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-200">
              <Check size={16} className="mt-0.5 text-emerald-400 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="mt-9 flex flex-wrap gap-3">
          <a href="#contact" data-testid="social-hero-primary" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-black px-7 py-4 transition-all hover:-translate-y-0.5 shadow-[0_15px_40px_-10px_rgba(34,211,238,0.7)]">
            Book My Free Content Audit <ArrowRight size={18} />
          </a>
          <a href="#pricing" data-testid="social-hero-secondary" className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-bold px-7 py-4 transition backdrop-blur-md">
            See The Packages
          </a>
        </div>
      </div>
      <div className="lg:col-span-5 relative h-[560px] flex items-center justify-center fade-up">
        <div className="absolute inset-0 grid place-items-center pointer-events-none">
          <div className="h-[420px] w-[420px] rounded-full bg-gradient-to-br from-cyan-500/15 to-fuchsia-500/15 blur-[80px]" />
        </div>
        <img
          src="/social/1.png"
          alt="Done-for-you Reels, Stories, Feed Posts, Carousels, and monthly content planning for local businesses"
          loading="eager"
          className="relative z-10 w-full h-auto max-h-[560px] object-contain rounded-3xl shadow-[0_30px_90px_-20px_rgba(34,211,238,0.35)] ring-1 ring-white/10"
        />
      </div>
    </div>
  </section>
);

/* ===================== PROBLEM ===================== */
const Problem = () => {
  const cards = [
    { icon: Clock, title: "No Time To Post", body: "You’re busy serving customers, answering calls, managing staff, and running daily operations." },
    { icon: Calendar, title: "No Content Strategy", body: "Random posts do not build trust. Your content needs a plan, a message, and a consistent schedule." },
    { icon: Settings2, title: "Editing Takes Too Long", body: "A simple Reel can take hours when you have to record, cut, caption, design, and post it yourself." },
    { icon: TrendingUp, title: "Competitors Look More Active", body: "When customers search online, they trust the business that looks alive, current, and professional." },
  ];
  return (
    <section id="problem" data-testid="social-problem" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-6 order-1">
            <div className="text-left fade-up">
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-black tracking-widest uppercase bg-cyan-50 text-cyan-700 border border-cyan-100">
                <Sparkles size={12} className="text-cyan-500" /> The reality
              </div>
              <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight text-slate-900">
                Your Business Needs Content. But You’re Already Busy Running The Business.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
                Most business owners know they should be posting more, but they get stuck because they don’t
                have time to plan, record, edit, design, write captions, or stay consistent.
              </p>
            </div>
          </div>
          <div className="lg:col-span-6 order-2 fade-up">
            <img
              src="/social/2.png"
              alt="Overwhelmed local business owner surrounded by social media tasks, notifications, and content demands"
              loading="lazy"
              className="w-full h-auto rounded-3xl shadow-[0_30px_80px_-20px_rgba(15,23,42,0.25)] ring-1 ring-slate-200"
            />
          </div>
        </div>
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {cards.map((c, i) => {
            const Ic = c.icon;
            return (
              <div key={i} className="group h-full rounded-3xl border border-slate-100 bg-white p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition fade-up">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 text-white shadow-lg">
                  <Ic size={22} />
                </span>
                <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{c.title}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{c.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ===================== SOLUTION ===================== */
const Solution = () => {
  const items = [
    { icon: Film, t: "Reels designed to grab attention" },
    { icon: ImageIcon, t: "Stories to keep your audience engaged" },
    { icon: Layout, t: "Posts to educate, promote, and build trust" },
    { icon: Layers, t: "Carousels for offers, services, before/after, FAQs" },
    { icon: Megaphone, t: "Captions, hooks, and CTAs included" },
    { icon: Calendar, t: "Monthly content calendar included" },
    { icon: UserCircle2, t: "Optional AI avatar clone for scalable video" },
    { icon: Eye, t: "Content ideas built around your actual business" },
  ];
  return (
    <section id="solution" data-testid="social-solution" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="The shortcut"
          title="We Turn Your Business Into A Content Machine."
          lede="The Social Media Care Package is a monthly content system where we plan, create, design, and organize your social media so your business stays active and professional online."
          testid="social-solution-header"
        />
        <p className="mt-6 text-center font-display text-xl sm:text-2xl font-bold text-slate-900 max-w-3xl mx-auto fade-up">
          You focus on running the business.{" "}
          <span className="bg-gradient-to-r from-cyan-500 to-fuchsia-500 bg-clip-text text-transparent">
            We help keep your brand showing up online.
          </span>
        </p>
        <div className="mt-14 fade-up">
          <img
            src="/social/3.png"
            alt="Content machine workflow — planning, creating, organizing, scheduling, and publishing social media content for local businesses"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(15,23,42,0.35)] ring-1 ring-slate-200"
          />
        </div>
        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => {
            const Ic = it.icon;
            return (
              <div key={i} className="rounded-3xl bg-white border border-slate-100 p-5 hover:shadow-xl transition fade-up flex items-start gap-3">
                <span className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-2xl bg-cyan-50 text-cyan-600">
                  <Ic size={20} />
                </span>
                <div className="font-display font-bold text-slate-900 text-[15px] leading-snug pt-1">{it.t}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ===================== RESULTS / DASHBOARD PREVIEW ===================== */
const ResultsPreview = () => (
  <section data-testid="social-results-preview" className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden">
    <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
    <div className="absolute -top-40 -right-20 h-[520px] w-[520px] rounded-full bg-fuchsia-600/20 blur-[140px] pointer-events-none" />
    <div className="absolute -bottom-40 -left-20 h-[520px] w-[520px] rounded-full bg-cyan-500/20 blur-[140px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeader
        dark
        eyebrow="What consistent content looks like"
        title="Consistency. Momentum. A Business That Looks Alive."
        lede="When your content goes out every week, your business finally looks active, professional, and trusted online — the kind of presence customers check before they ever call."
        testid="social-results-preview-header"
      />
      <div className="mt-12 fade-up">
        <img
          src="/social/visual-b.png"
          alt="Premium content dashboard preview showing consistent posting, growing reach, audience mix, engagement trend, and content calendar momentum"
          loading="lazy"
          className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(34,211,238,0.4)] ring-1 ring-white/10"
        />
        <p className="mt-6 text-center text-xs text-slate-400 max-w-2xl mx-auto italic">
          Illustrative preview. Individual results vary — we focus on consistency, visibility, and professional presence,
          not guaranteed follower, sales, or engagement numbers.
        </p>
      </div>
    </div>
  </section>
);

/* ===================== PRICING ===================== */
const Pricing = () => (
  <section id="pricing" data-testid="social-pricing" className="py-24 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeader
        eyebrow="Pricing"
        title="Choose Your Monthly Social Media Care Package"
        lede="Every package is built to keep your local business looking active, professional, and trustworthy online — without you planning, filming, editing, or captioning a thing."
        testid="social-pricing-header"
      />

      <div className="mt-14 fade-up">
        <img
          src="/social/4.png"
          alt="Monthly deliverables included in every Social Media Care Package — Reels, Stories, Feed Posts, Carousels, and a planned content calendar"
          loading="lazy"
          className="w-full h-auto rounded-3xl shadow-[0_30px_90px_-20px_rgba(15,23,42,0.2)] ring-1 ring-slate-200"
        />
      </div>

      <div className="mt-16 grid lg:grid-cols-3 gap-6 items-stretch">
        <PriceCard
          testid="price-starter"
          name="Starter Presence"
          price="$697"
          badge="Entry Package"
          bestFor="Best for businesses that need basic consistency and want to finally look active online."
          items={[
            "4 Reels per month",
            "8 Stories per month",
            "4 Feed Posts per month",
            "2 Carousel Posts per month",
            "Captions included",
            "Basic monthly content calendar",
            "1 platform focus: Instagram or Facebook",
            "Client provides photos/videos",
            "Monthly performance snapshot",
          ]}
          cta="Start With Starter"
          note="Perfect if you need consistency without a big monthly commitment."
          accent="amber"
        />
        <PriceCard
          testid="price-growth"
          name="Local Growth"
          price="$1,297"
          highlight
          badge="Most Popular · Best Value"
          bestFor="Best for most local businesses that want to post consistently, build trust, and save time without doing content themselves."
          items={[
            "10 Reels per month",
            "18 Stories per month",
            "8 Feed Posts per month",
            "4 Carousel Posts per month",
            "Captions, hooks, and CTA copy included",
            "Monthly content calendar",
            "Instagram + Facebook posting plan",
            "TikTok and YouTube Shorts repurposing guidance",
            "Monthly analytics snapshot",
            "1 monthly strategy call",
            "1 revision round per content batch",
          ]}
          cta="Choose Local Growth"
          note="Best value for most local businesses — the smartest fit without jumping to a high-volume plan."
        />
        <PriceCard
          testid="price-dominator"
          name="Market Builder"
          price="$2,497"
          badge="Highest Volume"
          bestFor="Best for businesses that want to look highly active and dominate their local online presence."
          items={[
            "20 Reels per month",
            "36 Stories per month",
            "12 Feed Posts per month",
            "7 Carousel Posts per month",
            "Captions, hooks, and CTA copy included",
            "Full monthly content calendar",
            "Instagram, Facebook, TikTok, and YouTube Shorts content plan",
            "Monthly strategy call",
            "Monthly analytics report",
            "Offer and content angle support",
            "Review/testimonial content ideas",
            "FAQ-based content ideas",
            "Priority production",
          ]}
          cta="Build My Market Presence"
          note="For businesses ready to take content seriously and show up more than their competitors."
          accent="fuchsia"
        />
      </div>
      <p className="mt-10 text-center text-slate-600 max-w-2xl mx-auto fade-up">
        Not sure which package fits?{" "}
        <a href="#contact" className="text-cyan-700 font-semibold underline-offset-4 hover:underline">
          Book a free content audit
        </a>{" "}
        and we’ll recommend the best package based on your business, goals, and current online presence.
      </p>
    </div>
  </section>
);

/* ===================== OPTIONAL ADD-ONS ===================== */
const AddOns = () => {
  const addons = [
    {
      icon: Film,
      name: "Content Capture Shoot",
      price: "$497 – $997 / shoot",
      body: "We come capture photos and videos for your business so we have fresh content to work with.",
    },
    {
      icon: Flame,
      name: "Extra Reel Pack",
      price: "$297 / 4 Reels",
      body: "Add more short-form videos when you have a promotion, event, launch, or busy season.",
    },
    {
      icon: Calendar,
      name: "Posting & Scheduling",
      price: "$297 / month",
      body: "We schedule and organize approved content so you do not have to manually post everything yourself.",
    },
    {
      icon: MessageCircle,
      name: "DM / Comment Management",
      price: "Custom Quote",
      body: "For businesses that want help responding to comments, messages, and online engagement.",
    },
    {
      icon: Megaphone,
      name: "Paid Ads Management",
      price: "Custom Quote",
      body: "Organic content packages do not include paid ads. Ad campaigns can be added separately.",
    },
  ];
  return (
    <section data-testid="social-addons" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Optional Add-Ons"
          title="Need More Content Or Hands-On Support?"
          lede="Mix and match these into any monthly package whenever your business needs more firepower."
          testid="social-addons-header"
        />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {addons.map((a, i) => {
            const Ic = a.icon;
            return (
              <div
                key={i}
                data-testid={`addon-${i}`}
                className="group h-full rounded-3xl border border-slate-100 bg-white p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition fade-up flex flex-col"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 text-white shadow-lg">
                    <Ic size={22} />
                  </span>
                  <span className="text-xs font-black uppercase tracking-widest text-cyan-700 bg-cyan-50 border border-cyan-100 rounded-full px-3 py-1 whitespace-nowrap">
                    {a.price}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{a.name}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed flex-1">{a.body}</p>
                <a
                  href="#contact"
                  data-testid={`addon-${i}-cta`}
                  className="mt-5 inline-flex items-center gap-2 text-cyan-700 font-bold text-sm hover:gap-3 transition-all"
                >
                  Add to my package <ArrowRight size={14} />
                </a>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ===================== VALUE STACK ===================== */
const ValueStack = () => {
  const rows = [
    { name: "Starter Presence", pieces: 18, price: 697, tag: "Entry Package" },
    { name: "Local Growth", pieces: 40, price: 1297, tag: "Most Popular · Best Value For Most", recommended: true },
    { name: "Market Builder", pieces: 75, price: 2497, tag: "Highest Volume" },
  ];
  return (
    <section data-testid="social-value-stack" className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden">
      <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
      <div className="absolute -top-40 right-0 h-96 w-96 rounded-full bg-fuchsia-600/20 blur-3xl pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-5 lg:px-8">
        <SectionHeader
          dark
          eyebrow="Real value, broken down"
          title="Consistency. Trust. Time Saved."
          lede="The real value is not just the cost per post. It is finally having a business that looks active, professional, and trustworthy online without you spending nights and weekends trying to make content yourself."
          testid="social-value-header"
        />
        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {rows.map((r, i) => {
            const cpp = Math.round(r.price / r.pieces);
            return (
              <div
                key={i}
                data-testid={`value-row-${i}`}
                className={`relative rounded-3xl p-7 fade-up ${
                  r.recommended
                    ? "bg-gradient-to-br from-cyan-500/15 to-fuchsia-500/15 border border-cyan-400/40 ring-1 ring-cyan-400/40 scale-[1.02]"
                    : "glass-dark"
                }`}
              >
                <div className="text-xs font-black tracking-widest uppercase text-cyan-300">{r.name}</div>
                <div className="mt-3 font-display text-5xl font-black">
                  {r.pieces}
                  <span className="text-sm text-slate-400 font-medium ml-2">pieces/month</span>
                </div>
                <div className="mt-2 text-slate-300">${r.price.toLocaleString()}/month</div>
                <div className="mt-5 inline-flex items-baseline gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2">
                  <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">Approx</span>
                  <span className="font-display text-2xl font-black text-cyan-300">${cpp} / piece</span>
                </div>
                <div
                  className={`mt-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-black tracking-widest uppercase ${
                    r.recommended
                      ? "bg-cyan-400 text-slate-900"
                      : "bg-white/10 text-slate-200 border border-white/10"
                  }`}
                >
                  {r.recommended && <Star size={12} fill="currentColor" />} {r.tag}
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-10 text-center text-slate-300 max-w-3xl mx-auto fade-up leading-relaxed">
          <span className="text-white font-bold">Local Growth is the best fit for most local businesses</span>{" "}
          because it gives you consistent content, strong variety, and the best overall value — without jumping into
          a high-volume monthly package.
        </p>
      </div>
    </section>
  );
};

/* ===================== AI CLONE ===================== */
const AIClone = () => (
  <section id="ai-clone" data-testid="social-ai-clone" className="py-24 md:py-32 bg-white">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeader
        eyebrow="AI Avatar Service"
        title="Clone Yourself With AI And Create Content Without Recording Every Time"
        lede="We help create an AI version of you using your likeness, voice, and delivery style as closely as possible, so your business can produce more video content without you needing to physically record every single time."
        testid="social-ai-header"
      />

      <div className="mt-12 fade-up">
        <img
          src="/social/5.png"
          alt="AI avatar cloning a business owner — record once, then create professional-looking video content at scale across Instagram, TikTok, YouTube Shorts, and Facebook"
          loading="lazy"
          className="w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(34,211,238,0.35)] ring-1 ring-slate-200"
        />
      </div>

      {/* Visual mockup row */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto fade-up">
        {[
          { icon: Mic, label: "Record once" },
          { icon: Bot, label: "Clone your voice" },
          { icon: Zap, label: "Create content faster" },
          { icon: Flame, label: "Scale your presence" },
        ].map((s, i) => {
          const Ic = s.icon;
          return (
            <div key={i} className="rounded-2xl border border-slate-100 bg-gradient-to-b from-slate-50 to-white p-5 text-center">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 text-white shadow">
                <Ic size={20} />
              </span>
              <div className="mt-3 font-display font-bold text-slate-900 text-sm">{s.label}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 max-w-3xl mx-auto rounded-2xl bg-amber-50 border border-amber-200 p-5 text-sm text-slate-700 leading-relaxed fade-up">
        <span className="font-bold text-slate-900">A note on results:</span> AI avatar results depend on the
        quality of the footage, voice samples, lighting, camera angle, and training material provided. The goal
        is to create the closest professional version possible for social media content.
      </div>

      <div className="mt-14 fade-up">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100 px-3 py-1 text-[11px] font-black tracking-widest uppercase">
            <Sparkles size={12} className="text-cyan-500" /> How the AI avatar process works
          </div>
          <h3 className="font-display font-extrabold text-2xl sm:text-3xl mt-3 text-slate-900">
            Record. Clone. Generate. Publish.
          </h3>
        </div>
        <img
          src="/social/6.png"
          alt="AI avatar workflow — step 1 record once, step 2 clone voice and face, step 3 generate new videos, step 4 publish across Instagram, TikTok, YouTube Shorts, LinkedIn, and Facebook"
          loading="lazy"
          className="mt-8 w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(15,23,42,0.4)] ring-1 ring-slate-200"
        />
      </div>

      <div className="mt-16 grid lg:grid-cols-3 gap-6 items-stretch">
        <PriceCard
          testid="price-ai-starter"
          name="AI Avatar Starter Setup"
          price="$497"
          suffix=" one-time"
          bestFor="For business owners who want to test AI avatar content."
          items={[
            "AI avatar setup guidance",
            "Voice clone setup guidance",
            "3 test avatar videos",
            "Basic script template",
            "Basic usage guide",
          ]}
          cta="Start My AI Avatar"
          accent="cyan"
        />
        <PriceCard
          testid="price-ai-pro"
          name="AI Avatar Pro Setup"
          price="$997"
          suffix=" one-time"
          highlight
          badge="Most Popular"
          bestFor="For business owners who want a realistic content machine for consistent posting."
          items={[
            "AI avatar setup",
            "Voice clone setup",
            "Avatar delivery testing",
            "Branded background/template",
            "10 AI avatar videos",
            "Hooks and captions included",
            "Usage guide",
          ]}
          cta="Build My AI Clone"
          accent="fuchsia"
        />
        <PriceCard
          testid="price-ai-elite"
          name="AI Avatar Elite Setup"
          price="$1,997"
          suffix=" one-time"
          badge="Best For Scaling"
          bestFor="For experts, coaches, service providers, and local brands ready to scale content."
          items={[
            "Premium avatar setup",
            "Voice clone setup",
            "Brand-style content system",
            "25 AI avatar videos",
            "Multiple video templates",
            "FAQ scripts",
            "Offer scripts",
            "Educational scripts",
            "Captions included",
            "2 revision rounds",
          ]}
          cta="Scale With My AI Clone"
          accent="amber"
        />
      </div>
    </div>
  </section>
);

/* ===================== AI MONTHLY ADD-ON ===================== */
const AIAddOn = () => (
  <section data-testid="social-ai-addon" className="py-24 md:py-32 bg-slate-50">
    <div className="max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeader
        eyebrow="AI Avatar Monthly"
        title="Keep Your AI Avatar Posting Every Month"
        testid="social-ai-addon-header"
      />
      <div className="mt-14 grid lg:grid-cols-3 gap-6 items-stretch">
        <PriceCard
          testid="price-ai-monthly-starter"
          name="AI Starter"
          price="$497"
          bestFor="Steady AI-driven posting to stay visible."
          items={[
            "6 AI avatar videos per month",
            "Basic scripts",
            "Captions included",
          ]}
          cta="Start AI Monthly"
        />
        <PriceCard
          testid="price-ai-monthly-growth"
          name="AI Growth"
          price="$997"
          highlight
          badge="Most Popular"
          bestFor="More volume, sharper hooks, monthly direction."
          items={[
            "15 AI avatar videos per month",
            "Hooks, scripts, and captions",
            "Monthly content direction",
          ]}
          cta="Grow AI Monthly"
          accent="fuchsia"
        />
        <PriceCard
          testid="price-ai-monthly-scale"
          name="AI Scale"
          price="$1,497"
          bestFor="High-output AI content engine with priority delivery."
          items={[
            "30 AI avatar videos per month",
            "Hooks, scripts, captions, and CTAs",
            "Monthly content plan",
            "Priority delivery",
          ]}
          cta="Scale AI Monthly"
          accent="amber"
        />
      </div>
    </div>
  </section>
);

/* ===================== BUNDLE TRANSITION (VISUAL C) ===================== */
const BundleTransition = () => (
  <section data-testid="social-bundle-transition" className="py-24 md:py-32 bg-white relative overflow-hidden">
    <div className="max-w-6xl mx-auto px-5 lg:px-8">
      <SectionHeader
        eyebrow="The transformation"
        title="From Outdated And Inactive To Modern, Active, And Trustworthy."
        lede="A better website is just the start. Pair it with consistent social content and AI-powered video, and your business goes from looking forgotten online to looking like the obvious choice in your market."
        testid="social-bundle-transition-header"
      />
      <div className="mt-14 fade-up">
        <img
          src="/social/visual-c.png"
          alt="Before and after transformation — an outdated, inactive business website and social presence on the left, a modern, active, trustworthy online presence with consistent content and engaged audience on the right"
          loading="lazy"
          className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(15,23,42,0.35)] ring-1 ring-slate-200"
        />
      </div>
    </div>
  </section>
);

/* ===================== BUNDLE ===================== */
const Bundle = () => (
  <section id="bundle" data-testid="social-bundle" className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden">
    <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
    <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-cyan-500/15 blur-[140px] pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
      <SectionHeader
        dark
        eyebrow="The Full Revival System"
        title="The Complete Online Presence Revival Bundle"
        lede="For businesses that want the full upgrade: a better website, consistent social media, AI-powered content, and monthly support."
        testid="social-bundle-header"
      />

      <div className="mt-12 fade-up">
        <img
          src="/social/7.png"
          alt="Everything included in the Complete Online Presence Revival Bundle — professional website, monthly social media content, AI-powered video, content calendar, and ongoing support"
          loading="lazy"
          className="w-full h-auto rounded-3xl shadow-[0_40px_140px_-30px_rgba(34,211,238,0.35)] ring-1 ring-white/10"
        />
      </div>

      <div className="mt-16 grid lg:grid-cols-3 gap-6 items-stretch">
        {[
          {
            testid: "bundle-launch",
            name: "Launch Revival",
            price: "$2,997",
            suffix: " setup",
            monthly: "$997/month",
            bestFor: "Businesses that need a professional online foundation.",
            items: [
              "1-page landing page",
              "Basic website copy",
              "Starter Presence social package",
              "Monthly website care",
              "Basic reporting",
            ],
            cta: "Start My Revival",
            accent: "cyan",
          },
          {
            testid: "bundle-growth",
            name: "Growth Revival",
            price: "$4,997",
            suffix: " setup",
            monthly: "$1,997/month",
            highlight: true,
            badge: "Recommended",
            bestFor: "A better website, consistent content, and AI-powered visibility.",
            items: [
              "Custom landing page or small website",
              "Local Growth social package",
              "AI Avatar Starter setup",
              "5 AI avatar videos",
              "Monthly content calendar",
              "Monthly strategy call",
              "Basic website updates/support",
            ],
            cta: "Choose Growth Revival",
            accent: "fuchsia",
          },
          {
            testid: "bundle-takeover",
            name: "Market Revival",
            price: "$6,997",
            suffix: " setup",
            monthly: "$2,997/month",
            badge: "Best Value",
            bestFor: "The strongest presence without hiring an in-house marketing team.",
            items: [
              "Premium landing page or custom website",
              "Market Builder social package",
              "AI Avatar Pro setup",
              "10 AI avatar videos",
              "Monthly content calendar",
              "Monthly strategy call",
              "Offer positioning",
              "Review/testimonial content strategy",
              "Priority support",
            ],
            cta: "Build My Full Revival System",
            accent: "amber",
          },
        ].map((b) => (
          <div
            key={b.testid}
            data-testid={b.testid}
            className={`relative h-full rounded-[2rem] p-7 sm:p-8 fade-up flex flex-col ${
              b.highlight
                ? "bg-gradient-to-br from-cyan-500/10 to-fuchsia-500/10 border border-cyan-400/30 ring-1 ring-cyan-400/40 scale-[1.02]"
                : "glass-dark"
            }`}
          >
            {b.badge && (
              <div className={`absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 rounded-full ${b.highlight ? "bg-cyan-400 text-slate-900" : "bg-amber-400 text-slate-900"} px-4 py-1 font-black text-[11px] tracking-widest uppercase shadow-lg`}>
                <Sparkles size={12} /> {b.badge}
              </div>
            )}
            <div className="text-xs uppercase tracking-widest font-black text-cyan-300">{b.name}</div>
            <div className="mt-3 flex items-baseline gap-2 flex-wrap">
              <span className="font-display text-4xl sm:text-5xl font-black leading-none">{b.price}</span>
              <span className="text-slate-400 text-sm">{b.suffix}</span>
            </div>
            <div className="mt-1 text-cyan-300 font-bold">+ {b.monthly}</div>
            <p className="mt-3 text-sm text-slate-300 italic leading-relaxed">{b.bestFor}</p>
            <ul className="mt-6 space-y-2.5 flex-1">
              {b.items.map((t, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px] text-slate-200 leading-snug">
                  <Check size={16} className="mt-1 text-cyan-400 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <a
              href="#contact"
              className={`mt-7 inline-flex items-center justify-center gap-2 rounded-full font-black px-6 py-3.5 transition hover:-translate-y-0.5 ${
                b.highlight ? "bg-cyan-400 hover:bg-cyan-300 text-slate-900" : "bg-white/10 hover:bg-white/20 border border-white/15 text-white"
              }`}
            >
              {b.cta} <ArrowRight size={16} />
            </a>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ===================== WHAT'S INCLUDED / NOT INCLUDED ===================== */
const IncludedExcluded = () => {
  const included = [
    "Content planning",
    "Content design",
    "Captions and hooks",
    "Monthly content calendar",
    "AI-assisted content creation",
    "Basic reporting",
    "Revisions based on package",
  ];
  const notIncluded = [
    "Paid advertising budget",
    "Daily DM management",
    "Daily comment management",
    "Professional video shoots unless added",
    "Influencer campaigns",
    "Advanced SEO",
    "Advanced CRM automations",
  ];
  return (
    <section data-testid="social-included" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Full transparency"
          title="What’s Included And What’s Not Included"
          lede="So you know exactly what you’re paying for — and what would be a custom add-on."
          testid="social-included-header"
        />

        <div className="mt-14 fade-up">
          <img
            src="/social/visual-a.png"
            alt="Service categories included in the Social Media Care Package — Reels, Stories, Feed Posts, Carousel Posts, AI Avatar, Website, Calendar, Analytics, Strategy, and Support"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(15,23,42,0.4)] ring-1 ring-slate-800 bg-[#0B0F19]"
          />
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 p-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 text-emerald-700 border border-emerald-200 px-3 py-1 text-[11px] font-black tracking-widest uppercase">
              <Check size={12} /> Included
            </div>
            <ul className="mt-6 space-y-3">
              {included.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-800 leading-relaxed">
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  <span className="font-medium">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-900/5 text-slate-700 border border-slate-200 px-3 py-1 text-[11px] font-black tracking-widest uppercase">
              <Lock size={12} /> Not Automatically Included
            </div>
            <ul className="mt-6 space-y-3">
              {notIncluded.map((t, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 leading-relaxed">
                  <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500 font-black text-xs">
                    —
                  </span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-slate-500 leading-relaxed">
              Anything in this list can be added as a custom add-on or quoted separately.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ===================== HOW IT WORKS ===================== */
const HowItWorks = () => {
  const steps = [
    { n: "01", title: "Book Your Free Content Audit", body: "We review your current website, social media, offers, and online presence." },
    { n: "02", title: "Choose Your Package", body: "We recommend the best package based on your business goals and content volume." },
    { n: "03", title: "We Build Your Content System", body: "Content calendar, posts, captions, and your monthly content prepared." },
    { n: "04", title: "You Approve, We Launch", body: "You review, request changes if needed, and we help your brand show up consistently." },
    { n: "05", title: "Keep Growing Every Month", body: "We track performance, adjust content direction, and keep your business active online." },
  ];
  return (
    <section data-testid="social-how" className="py-24 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <SectionHeader eyebrow="How it works" title="How The Process Works" testid="social-how-header" />
        <ol className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {steps.map((s, i) => (
            <li key={i} className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm fade-up">
              <span className="font-display text-3xl font-black bg-gradient-to-r from-cyan-500 to-fuchsia-500 bg-clip-text text-transparent">{s.n}</span>
              <h3 className="mt-2 font-display text-lg font-bold text-slate-900 leading-snug">{s.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

/* ===================== WHO + WHY ===================== */
const WhoFor = () => {
  const tags = [
    "Service businesses", "Retail stores", "Restaurants", "Beauty professionals",
    "Med spas", "Contractors", "Realtors", "Gyms", "Auto businesses", "Cellphone stores",
    "Doctors and dentists", "Coaches and consultants", "Personal brands", "Local experts",
  ];
  return (
    <section data-testid="social-whofor" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 lg:px-8">
        <SectionHeader
          eyebrow="Who it's for"
          title="Built For Local Businesses That Want To Look Alive Online"
          lede="If people check your Instagram, Facebook, TikTok, or website before deciding to trust you, this service is for you."
          testid="social-whofor-header"
        />
        <div className="mt-12 flex flex-wrap justify-center gap-2.5 fade-up">
          {tags.map((t, i) => (
            <span key={i} className="inline-flex items-center px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-800 font-medium shadow-sm hover:border-cyan-400 hover:text-cyan-700 hover:shadow-md transition">
              <Building2 size={14} className="mr-2 text-cyan-500" /> {t}
            </span>
          ))}
        </div>
        <div className="mt-14 fade-up">
          <img
            src="/social/8.png"
            alt="Local business categories we serve — restaurants, barber shops, salons, med spas, contractors, boutiques, realtors, fitness studios, cellphone stores, and service pros"
            loading="lazy"
            className="w-full h-auto rounded-3xl shadow-[0_40px_120px_-30px_rgba(15,23,42,0.3)] ring-1 ring-slate-200"
          />
        </div>
      </div>
    </section>
  );
};

const WhyMatters = () => (
  <section data-testid="social-why" className="py-24 md:py-32 bg-white">
    <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center fade-up">
      <SectionHeader eyebrow="Why this matters" title="Your Online Presence Is Usually The First Impression" testid="social-why-header" />
      <p className="mt-6 text-slate-600 leading-relaxed max-w-2xl mx-auto">
        Before someone calls, books, visits, or buys, they usually check you online. If your
        website looks outdated, your social media looks inactive, or your content does not show
        what makes you different, potential customers may move on to a competitor.
      </p>
      <p className="mt-4 text-slate-700 leading-relaxed max-w-2xl mx-auto font-semibold">
        This service helps your business look active, professional, trustworthy, and current.
      </p>
    </div>
  </section>
);

/* ===================== TRUST BADGES ===================== */
const TrustBadges = () => {
  const badges = [
    { icon: Sparkles, t: "Done-For-You Content" },
    { icon: Bot, t: "AI-Powered Creation" },
    { icon: Building2, t: "Built For Local Businesses" },
    { icon: Calendar, t: "Monthly Strategy" },
    { icon: Layers, t: "Website + Social Bundle" },
    { icon: ShieldCheck, t: "Free Content Audit" },
  ];
  return (
    <section data-testid="social-trust" className="py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-5 lg:px-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {badges.map((b, i) => {
          const Ic = b.icon;
          return (
            <div key={i} className="rounded-2xl bg-white border border-slate-100 p-4 text-center fade-up">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-fuchsia-500 text-white">
                <Ic size={18} />
              </span>
              <div className="mt-2 text-xs font-display font-bold text-slate-900 leading-tight">{b.t}</div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

/* ===================== FAQ ===================== */
const FAQ = () => {
  const faqs = [
    { q: "Do I have to record videos myself?", a: "Not always. For regular content, we can use a mix of business photos, service footage, customer-friendly graphics, AI-assisted creative, text-based Reels, and branded content. If you choose the AI avatar package, we can also help create content using your AI clone." },
    { q: "Can you post the content for me?", a: "Yes — posting and scheduling can be included depending on the package and platform access. We can also provide the content ready for you or your team to post." },
    { q: "Will the AI avatar look exactly like me?", a: "The goal is to make it look and sound as close to you as possible, but results depend on the quality of the videos, photos, voice samples, lighting, and training material provided. We focus on creating a professional, realistic, and usable version for content creation." },
    { q: "What platforms do you create content for?", a: "Instagram, Facebook, TikTok, YouTube Shorts, LinkedIn, and Google Business Profile depending on the business and package." },
    { q: "Do you write captions too?", a: "Yes. Captions, hooks, and calls to action are included in the packages." },
    { q: "Do you help with strategy?", a: "Yes. The Growth, Market Dominator, and bundle packages include stronger strategy, content direction, and performance review." },
    { q: "Is there a contract?", a: "Flexible monthly options available. Final terms are confirmed before starting." },
    { q: "Can I customize a package?", a: "Yes. Businesses can request a custom quote if they need a different content volume, extra platforms, paid ads, extra AI avatar videos, or a website build." },
  ];
  return (
    <section id="faq" data-testid="social-faq" className="py-24 md:py-32 bg-slate-50">
      <div className="max-w-3xl mx-auto px-5 lg:px-8">
        <SectionHeader eyebrow="FAQ" title="Answers to common questions" testid="social-faq-header" />
        <Accordion type="single" collapsible className="mt-12 rounded-3xl bg-white border border-slate-100 shadow-sm divide-y divide-slate-100 overflow-hidden">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`f-${i}`} className="px-6 sm:px-8 border-b-0">
              <AccordionTrigger data-testid={`social-faq-trigger-${i}`} className="text-left font-display font-bold text-slate-900 text-base sm:text-lg py-5 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 text-base leading-relaxed pb-6 pr-2">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

/* ===================== CONTACT ===================== */
const SERVICE_OPTIONS = [
  "Social Media Care Package",
  "AI Avatar Clone",
  "Website + Social Media Bundle",
  "Not Sure Yet",
];

const initialForm = {
  name: "",
  business_name: "",
  phone: "",
  email: "",
  website_url: "",
  social_url: "",
  service_interest: SERVICE_OPTIONS[0],
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.business_name.trim() || !form.phone.trim() || !form.email.trim()) {
      toast.error("Please fill in your name, business, phone, and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/social-leads`, form);
      toast.success("Thank you! Your audit request has been received. We’ll review and contact you shortly.");
      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try again or contact us directly at info@instantsiterevival.com.");
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="social-contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5 fade-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-100 px-3 py-1 text-[11px] font-black tracking-widest uppercase">
            <Sparkles size={12} /> Free Content Audit
          </div>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight">
            Ready To Stop Guessing What To Post?
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Book a free content audit and we’ll show you how your business can look more active,
            professional, and trustworthy online.
          </p>
          <div className="mt-8 space-y-3">
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" data-testid="social-contact-whatsapp" className="flex items-center justify-between rounded-2xl bg-[#25D366] hover:bg-[#1eb558] text-white px-5 py-4 font-bold transition-all hover:-translate-y-0.5 shadow-lg">
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-white/15 inline-flex items-center justify-center">
                  <MessageCircle size={20} />
                </span>
                <span className="flex flex-col text-left">
                  Message Us Now
                  <span className="text-xs font-medium opacity-90">786-575-6919 · WhatsApp</span>
                </span>
              </span>
              <Send size={18} />
            </a>
            <a href="https://calendly.com/instantsiterevival-info/30min" target="_blank" rel="noopener noreferrer" data-testid="social-contact-booking" className="flex items-center justify-between rounded-2xl border border-slate-200 hover:border-cyan-400 hover:text-cyan-700 text-slate-900 px-5 py-4 font-bold transition-all bg-white">
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-600 inline-flex items-center justify-center">
                  <Calendar size={20} />
                </span>
                <span className="flex flex-col text-left">
                  Book a Quick Call
                  <span className="text-xs font-medium text-slate-500">30-minute slot · Calendly</span>
                </span>
              </span>
              <Send size={18} />
            </a>
            <a href="mailto:info@instantsiterevival.com" data-testid="social-contact-email" className="flex items-center justify-between rounded-2xl bg-slate-900 hover:bg-slate-800 text-white px-5 py-4 font-bold transition-all hover:-translate-y-0.5">
              <span className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-xl bg-white/10 inline-flex items-center justify-center">
                  <Mail size={20} />
                </span>
                <span className="flex flex-col text-left">
                  Email Us
                  <span className="text-xs font-medium text-slate-300">info@instantsiterevival.com</span>
                </span>
              </span>
              <Send size={18} />
            </a>
          </div>
          <div className="mt-8 fade-up">
            <img
              src="/social/9.png"
              alt="Free content audit — our team reviews your website, social media, and online presence, then recommends a strategy to help your local business look active, professional, and trustworthy online"
              loading="lazy"
              className="w-full h-auto rounded-3xl shadow-[0_30px_90px_-20px_rgba(15,23,42,0.25)] ring-1 ring-slate-200"
            />
          </div>
        </div>

        <div className="lg:col-span-7 fade-up">
          <form data-testid="social-contact-form" onSubmit={onSubmit} className="rounded-3xl border border-slate-100 bg-gradient-to-b from-slate-50 to-white p-7 sm:p-9 shadow-xl space-y-5">
            {submitted && (
              <div data-testid="social-success-banner" className="flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3">
                <Sparkles size={20} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold">Thank you! Your audit request has been received.</div>
                  <div className="text-sm">We’ll review and contact you shortly. Want to chat now? Tap “Message Us Now”.</div>
                </div>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { id: "name", label: "Name *", placeholder: "Your full name", req: true },
                { id: "business_name", label: "Business Name *", placeholder: "e.g. Joe’s Auto Shop", req: true },
                { id: "phone", label: "Phone Number *", placeholder: "(555) 555-5555", req: true },
                { id: "email", label: "Email *", placeholder: "you@business.com", type: "email", req: true },
                { id: "website_url", label: "Website URL", placeholder: "https://yourbusiness.com", full: true },
                { id: "social_url", label: "Instagram or Social Media Link", placeholder: "https://instagram.com/yourbusiness", full: true },
              ].map((f) => (
                <div key={f.id} className={f.full ? "sm:col-span-2" : ""}>
                  <label htmlFor={`s-${f.id}`} className="text-slate-700 font-semibold text-sm">{f.label}</label>
                  <input
                    id={`s-${f.id}`}
                    name={f.id}
                    type={f.type || "text"}
                    data-testid={`social-input-${f.id}`}
                    value={form[f.id]}
                    onChange={onChange}
                    placeholder={f.placeholder}
                    required={f.req}
                    className="mt-1.5 w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400"
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label htmlFor="s-service" className="text-slate-700 font-semibold text-sm">Which service are you interested in?</label>
                <select
                  id="s-service"
                  name="service_interest"
                  data-testid="social-input-service"
                  value={form.service_interest}
                  onChange={onChange}
                  className="mt-1.5 w-full h-11 rounded-md border border-slate-200 bg-white px-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400"
                >
                  {SERVICE_OPTIONS.map((s) => (<option key={s} value={s}>{s}</option>))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="s-message" className="text-slate-700 font-semibold text-sm">Message</label>
                <textarea
                  id="s-message"
                  name="message"
                  data-testid="social-input-message"
                  value={form.message}
                  onChange={onChange}
                  placeholder="Tell us a little about your business and what you’re looking for…"
                  className="mt-1.5 w-full min-h-[120px] rounded-md border border-slate-200 bg-white px-3 py-2 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/40 focus:border-cyan-400"
                />
              </div>
            </div>

            <button type="submit" disabled={submitting} data-testid="social-submit" className="w-full h-12 rounded-full bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:from-cyan-400 hover:to-fuchsia-400 text-white font-black text-base shadow-[0_15px_40px_-10px_rgba(34,211,238,0.6)] disabled:opacity-60">
              {submitting ? "Sending..." : "Book My Free Content Audit"}
            </button>
            <p className="text-xs text-slate-500 text-center">
              We’ll review your business, current presence, and goals — then recommend the best package.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

/* ===================== FINAL CTA ===================== */
const FinalCTA = () => (
  <section data-testid="social-final-cta" className="py-24 md:py-32 bg-[#0B0F19] text-white relative overflow-hidden">
    <div className="absolute inset-0 hero-grid opacity-40 pointer-events-none" />
    <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-cyan-500/15 blur-[140px] pointer-events-none" />
    <div className="relative max-w-3xl mx-auto px-5 lg:px-8 text-center fade-up">
      <h2 className="font-display font-extrabold text-3xl sm:text-5xl lg:text-6xl leading-[1.05]">
        Not Sure Which Package
        <span className="block mt-2 bg-gradient-to-r from-cyan-300 to-fuchsia-400 bg-clip-text text-transparent">
          Fits Your Business?
        </span>
      </h2>
      <p className="mt-6 text-slate-300 max-w-2xl mx-auto leading-relaxed">
        Book a free content audit and we’ll review your current website, social media, offers, and online
        presence. Then we’ll recommend the best package based on where your business is now and where you
        want to go.
      </p>
      <div className="mt-9 flex flex-wrap justify-center gap-3">
        <a href="#contact" data-testid="social-finalcta-primary" className="inline-flex items-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-black px-7 py-4 transition-all hover:-translate-y-0.5 shadow-[0_15px_40px_-10px_rgba(34,211,238,0.7)]">
          Book My Free Content Audit <ArrowRight size={18} />
        </a>
        <a href="#pricing" data-testid="social-finalcta-secondary" className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-bold px-7 py-4 transition backdrop-blur-md">
          View Packages
        </a>
      </div>
    </div>
  </section>
);

/* ===================== Mobile sticky CTA ===================== */
const MobileSticky = () => (
  <div data-testid="social-mobile-sticky" className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[#0B0F19]/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 flex items-center gap-2">
    <a href={WHATSAPP} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] text-white font-bold py-3 text-sm shadow-lg active:scale-[0.98]">
      <MessageCircle size={18} /> Message Us
    </a>
    <a href="#contact" className="flex-1 inline-flex items-center justify-center rounded-full bg-cyan-400 text-slate-900 font-bold py-3 text-sm active:scale-[0.98]">
      Free Audit
    </a>
  </div>
);

/* ===================== PAGE ===================== */
export default function Social() {
  useSEO({
    title: "Social Media Care Package | Done-For-You Content & AI Avatar Service",
    description:
      "Done-for-you social media for local businesses. Reels, Stories, Posts, and Carousels — Starter Presence $697, Local Growth $1,297 (Most Popular), Market Builder $2,497. Optional AI avatar setup from $497. Book a free content audit.",
    image: "https://instantsiterevival.com/og-social.png",
    path: "/social",
  });

  useEffect(() => {
    document.title = "Social Media Care Package | Done-For-You Content & AI Avatar Service";
    const els = document.querySelectorAll(".fade-up");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("in"); io.unobserve(e.target); } }),
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div data-testid="social-page" className="min-h-screen bg-white text-slate-900">
      <SocialNav />
      <main>
        <Hero />
        <Problem />
        <Solution />
        <ResultsPreview />
        <Pricing />
        <AddOns />
        <ValueStack />
        <AIClone />
        <AIAddOn />
        <BundleTransition />
        <Bundle />
        <IncludedExcluded />
        <HowItWorks />
        <WhoFor />
        <WhyMatters />
        <TrustBadges />
        <FAQ />
        <Contact />
        <FinalCTA />
      </main>
      <SocialFooter />
      <MobileSticky />
    </div>
  );
}
